/* 166 — `import React` is here for `react/react-in-jsx-scope`, which the
   committed eslint config still enforces and which the ratchet counts once per
   JSX element (`scripts/lint/eslint-ratchet.js`: a file's warning count may
   never rise). This brief adds JSX to this file, so the rule fires on every new
   element; putting React in scope is the rule's OWN remedy rather than a way
   around it, it costs nothing at runtime (the automatic JSX transform does not
   need it), and it clears the same debt for every element already here. */
import React from "react";
import { useState, useEffect, Fragment } from "react";
import type { ReactNode, CSSProperties } from "react";
import {
  // 216 addendum 4 §8 — `Pencil`, `Trash2`, `Download` and `Archive` left with
  // the lines' edit, delete, .ics and archive actions (the deploy note §7.5).
  List,
  Grid3X3,
  Search,
  // BRIEF 102 §5b — `Play` left with the Agenda task row's timer button.
  CalendarPlus,
  // BRIEF 105 §3a — `File`, not `FileText`. See CaseTaskRow.tsx for the census.
  File,
  // 111 §3 — the note icon, same glyph as NotesToggle everywhere else.
  NotebookPen,
} from "lucide-react";
import {
  pm,
  // BRIEF 102 §5b — `fmtElapsed` left with it: the running-timer readout
  // was that button's other half.
  pname,
  queueScrollToEv,
  queueScrollToTk,
  eisenQuadrant,
} from "../utils";
import {
  LockButton,
  MyAllToggle,
  InitialsBadge,
  AddButton,
  openDocHref,
  JoinBtn,
} from "../components/SharedUI";
import { ArchivedHoleBadge } from "../components/SharedUI";
import Combobox from "../components/Combobox";
import { isEmailDocUrl, openEmailDocHref } from "../lib/emailLink"; // 126 §9
import { isArchivedCo, choosableCo } from "../lib/collaboratorState";
import { EisenBadge, eisenLabel } from "../EisenBadge";
import { usePermissions } from "../hooks/usePermissions";
import { useTaxonomy } from "../hooks/useTaxonomy";
import OutlookImportModal from "../components/OutlookImportModal";
import { getAgenda } from "../api";
import EventTypeChips from "../components/EventTypeChips";
import { eventTypeVisible } from "../lib/eventTypeFilter";
import { isNonWorkingDow } from "../lib/scheduleAxis"; // 132 §7
import { useTranslation } from "../i18n";
import { resolveSubtaskText } from "../lib/subtaskHelpers";
// 216 addendum 4 §8 — `SubtaskCounter` and `EventSubtasks` left with the lines'
// counters and drawers (a subtask is a row of its own line now, on its date).
// BRIEF 102 §2f — this view's own day counter, extracted so the matrix cards
// can take the form it already established instead of a new one.
import { dayDiff, dayCountdownLabel } from "../lib/dayCountdown";
import EventDayMark from "../components/EventDayMark"; // 136 §3
import { eventStatusOf } from "../lib/eventStatus"; // 136 §3
import { agendaRowTitles } from "../lib/matterLabel"; // 216 §1
import { RAD, FS, Z } from "../lib/tokens"; // §5b — `CT` was the running timer's white
// BRIEF 107a §2a — the one "this row has something" colour; this view mounts
// neither row component and would otherwise keep its own answer.
import { DRAWER_ICON_HAS, DRAWER_ICON_EMPTY } from "../components/RowDrawerToggle";
import LinkIcon from "../components/LinkIcon"; // 111 §5c
// 216 addendum 4 §8 [RULED 79] — `RowTimerBlock` (116 §3) left this view: no time, no timers on the Agenda page.
import TypeTagBox, { TYPE_CHIP } from "../components/TypeTagBox"; // 118 §8; 204 §7.1 — the chip's one definition
import { placeOf } from "../lib/placeName"; // 216 addendum 4 §8 — the place without its address

/* BRIEF 105 §4c — THE AGENDA'S HAIRLINE, ONCE.
   This value was written twice in this file: the day marker's trailing rule
   and the two-column grid's divider. §4c needs a rule on BOTH sides of the day
   marker, which would have made it four — so it becomes one declaration used
   by all three rather than a fourth copy.
   THE VALUE IS UNCHANGED. A LIGHTER rule than the month's `var(--br)` is what
   makes the two markers read as a hierarchy: the month is the heading, the day
   is the marker inside it. §4.9 bans hardcoded colours in NEW UI and this is
   the opposite move — an existing literal collected into one place instead of
   spread further. Turning it into a theme variable is a token question for the
   theme pass, not a layout change to smuggle in here. */
/* 216 addendum 4 §8 (2) [RULED 79] — THE LINES' COLUMNS, named once. A line is
   one grid whose rows share these tracks, and every line of a list has the same
   ones: the type tag's box is the mirror's constant width (139 §1), each icon
   one slot, the days counter one slot.
   216 addendum 6 §10 (1) [RULED 81] — AND THE LIST IS THE GRID: every line is a
   SUBGRID sharing its list's columns, so a column measures the same on every
   line by construction; the days column is `auto` with nothing hidden in it.
   216 addendum 7 §11 (1) [RULED 84] — AND THE PAGE IS THE GRID. Laurent, 16/10:
   a case name « should be in full since nothing else on that row except the
   place, which truncates », and the columns line up « down the whole page »
   (his 6). So:
   · ONE grid for the page's list (`.agPage`, App.css): every day a subgrid of
     it (the band with its padding and negative margin — measured to shift
     nothing), each day's two lists subgrids of the day, each line a subgrid of
     its list. An `auto` column therefore measures the widest content the whole
     page shows in it — the days column the widest counter on the page, the
     type tag's column the longest code — on every day alike. Wide, the two
     kinds sit side by side on two copies of ONE track list (AG_COLS, below, a
     16 px track between them — `--ag-cols-wide`); narrow (App.css, 899 px) the
     page keeps one copy and each day's two lists stack on it (`--ag-cols-
     narrow`), so the columns line up across kinds as well;
   · the case name's column is `auto` and the description `minmax(AG_DESC_FLOOR,
     1fr)`: the name takes its whole width wherever the room allows, the
     description gives way and ellipsises — and below the floor the name
     ellipsises too [ADVISOR — a floor, so the description never vanishes]. The
     half share of addendum 6 (`fit-content(calc(… × ½))`) and its estimate of
     the fixed cells are gone with it. MEASURED in both engines before this line
     (the scratch probe): the columns' x equal across two days, a 626 px name
     ellipsised with the description at the floor, a 277 px one whole.
   The ONE track list, for both kinds: the tag · a 20 px and a 26 px slot (a
   task's Eisenhower badge and its assignee; an event's people area, 38 px —
   BADGE_AREA_W — spans the two) · the case name · the description · two icon
   slots · the link slot or the status pill (`auto`) · the days. */
const AG_ICON_W = 18;
/** The one column gap of the page's grid (the lines were 7 and 4 apart by kind; one grid, one gap — the tasks' 4:
 *  MEASURED at 1 440 px, each half of the page keeps about 270 px for the two texts once its fixed cells are paid, and
 *  two more pixels at each of eight gaps are 16 px of a case name). */
const AG_GAP = 4;
/** The description's floor [ADVISOR]: ~16 characters at the line's face — the description gives way (Laurent's rule),
 *  yet a long case name never leaves it nothing. MEASURED at 1 440 px: at 150 px the floor took a third of the
 *  texts' room and a 24-character case name (163 px) could not show whole; at 100 px it does. */
const AG_DESC_FLOOR = 100;
const AG_COLS = `auto 20px 26px auto minmax(${AG_DESC_FLOOR}px, 1fr) ${AG_ICON_W}px ${AG_ICON_W}px auto auto`;
/** The page grid's two track lists, handed to App.css as custom properties (the breakpoint is the stylesheet's). */
const AG_PAGE_VARS = { "--ag-cols-wide": `${AG_COLS} 16px ${AG_COLS}`, "--ag-cols-narrow": AG_COLS, "--ag-gap": `${AG_GAP}px` } as CSSProperties;
/** A day's list: a subgrid of the day on the page's columns, every line of it a subgrid in turn. */
const agList = (kind: "events" | "tasks"): CSSProperties => ({
  display: "grid",
  gridTemplateColumns: "subgrid",
  /* wide: the events on the first copy, the tasks on the second (App.css stacks both on one copy when narrow) */
  gridColumn: kind === "events" ? "1 / 10" : "11 / 20",
  alignContent: "start",
  alignSelf: "start",
  overflow: "hidden",
  minWidth: 0,
});
/** A day: a subgrid of the page spanning every column, so its marker, its lists and their lines share the tracks. */
const AG_DAY: CSSProperties = { display: "grid", gridTemplateColumns: "subgrid", gridColumn: "1 / -1" };
/** A line: a subgrid on its list's columns, with rows of its own. Its column gap is the page's, said explicitly:
 *  an event's line is an `.event-item`, whose class sets `gap: 8px`, and a subgrid with a gap of its own shifts every
 *  cell after the first by half the difference (MEASURED: 2 px against the task lines, narrow and wide alike). */
const agLine = (rows: number): CSSProperties => ({
  display: "grid",
  gridTemplateColumns: "subgrid",
  gridColumn: "1 / -1",
  gridTemplateRows: `repeat(${rows}, auto)`,
  columnGap: AG_GAP,
  alignItems: "center",
});
const AG_ELLIPSIS: CSSProperties = { minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };
const AG_CENTER: CSSProperties = { display: "inline-flex", alignItems: "center", justifyContent: "center" };
const AG_ICON_BTN: CSSProperties = { background: "none", border: "none", padding: "0 2px", display: "inline-flex", alignItems: "center", lineHeight: 0 };
/** The days counter's cell: the last column, spanning the line's rows, centred on them, at the right edge. */
const agDaysCell = (rows: number): CSSProperties => ({
  gridColumn: -2,
  gridRow: `1 / ${rows + 1}`,
  alignSelf: "center",
  justifySelf: "end",
  display: "inline-flex",
  alignItems: "center",
});
/** This view's own countdown colour ladder (136 §3 kept it this view's, apart from CaseEventRow's). */
const agDayColor = (diff: number): string =>
  diff === 1 ? "var(--rd)" : diff === 2 || diff === 3 ? "var(--am)" : diff >= 4 && diff <= 7 ? "var(--gn)" : "var(--tm)";
/** A task line's days counter — EventDayMark's counter half, without an event status to glyph. */
const agDays = (label: string | null, color: string, isPast: boolean) =>
  label === null ? null : (
    <span style={{ fontWeight: 700, fontSize: FS.s13, color, fontStyle: isPast ? "italic" : "normal", flexShrink: 0 }}>
      {label}
    </span>
  );
/** One line of the tasks column: a task on one of its dates, with the live subtasks due that date. */
type TkLine = { tsk: any; date: string; subs: any[]; own: boolean };

const AG_HAIRLINE = "1px solid rgba(255,255,255,.06)";

const AgView = ({
  /* Phase 31 (C4) — bell / Daily-Overview "N Outlook events to import"
     entries land here and auto-open the existing manual choice-list. */
  autoOpenImport = false,
  onAutoImportConsumed,
  currentUser,
  co,
  ag,
  sAg,
  ca,
  cc,
  agTypeFilt,
  setAgTypeFilt,
  agDateRange,
  setAgDateRange,
  agViewMode,
  setAgViewMode,
  // 132 §7 — the firm's working week and its legal holidays, both resolved in
  // App and read here the way every other surface reads them.
  schedAxis,
  holidays,
  // 133 §4 — the firm's calendar-display switches, resolved in App. Two of the
  // three are this view's: `agendaNonWorking` gates 132 §7(b)'s field and
  // `agendaAltBand` puts 132 §7(a)'s alternating band back within reach.
  calDisp,
  agMy,
  sAgMy,
  agCoFilt,
  sAgCoFilt,
  af,
  sAf,
  showEarlier,
  sShowEarlier,
  agMulti,
  setAgMulti,
  s,
  nav,
  tryNavCase,
  om,
  toast,
  etTag,
  evtLongestCode, // 139 §1 — the mirror's code, for the list rows' TypeTagBox
  coAvs,
  locIcon,
  /* 216 addendum 4 §8 [RULED 79] — `evToVevent`, `downloadIcs`, `activeTimers`,
     `startTimer`, `svTkData` and `setTkDelConfirm` left with the .ics download,
     the timer, the archive and the delete; addendum 6 §10 (4) took the six off
     App's mount too. 216 addendum 7 §11 (4) — and the seven this view never
     read (`DLBtn`, `activeTimer`, `delTk`, `getTimerDisplay`, `showDL`,
     `lookback`, `ju`) leave the mount and this list. */
  evtTypes,
  tk,
  tkTypes,
  TkStatusTag,
}: any) => {
  const { t, locale } = useTranslation();
  /* BRIEF 139 §1 — the mirror for this view's list rows, from the SAME `etTag`
     the visible chip uses. Typed on arrival although the props object is `any`
     (rule 4); null when the event taxonomy is empty. */
  const _longestCode: string = evtLongestCode || "";
  const _typeMirror: ReactNode = _longestCode ? etTag(_longestCode) : null;
  /* 216 addendum 7 §11 (1) [RULED 84] — the task tag's mirror by the same device: the longest code the firm's
     task taxonomy holds (a divider, `__div__*`, is not a code), hidden in every task line's tag box. */
  const _tkLongestCode: string = (Array.isArray(tkTypes) ? tkTypes : [])
    .map((x: { code?: unknown }) => String((x && x.code) || ""))
    .filter((c: string) => c && !c.startsWith("__div__"))
    .reduce((a: string, b: string) => (b.length > a.length ? b : a), "");
  const _tkTypeMirror: ReactNode = _tkLongestCode ? <span style={TYPE_CHIP}>{_tkLongestCode}</span> : null;
  /* 136 §3 — the firm's event statuses, for the mark at each row's right edge.
     Entries (not the tuple list): the glyph lives in `meta.icon`, and
     `useTaxonomyList`'s tuple shape carries no meta (taxonomyHelpers.ts:97). */
  const { entries: evStatusEntries } = useTaxonomy("event_status");
  /* 216 addendum 4 §8 — the item-level ladder (the priority letter) and the
     task-status entries (the archive icon's gate) left with the pieces that
     read them; `useTaxonomyList` stays for the filters below. */
  /* 195 §C.3e — this screen's search box (below) held its text in App's
     page-level `query` (`q`/`sQ` props); that state's box is gone and the
     text is the Agenda's own now. Reset by unmounting, as every navigation
     reset it before. */
  const [q, sQ] = useState("");
  const [agLocked, setAgLocked] = useState(true);
  const [outlookModalOpen, setOutlookModalOpen] = useState(false);
  // Phase 31 (C4) — bell / Daily-Overview entry points open the import
  // choice-list directly (one-shot; the flag is consumed on arrival).
  useEffect(() => {
    if (autoOpenImport) {
      setOutlookModalOpen(true);
      if (onAutoImportConsumed) onAutoImportConsumed();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenImport]);
  /* 216 addendum 4 §8 — the inline subtask lists (Phase 23's `openSubs`) left
     with the counters: a subtask is a row of its own line now, on its date. */
  /* 111 §3 — the note reveal, keyed the same `${kind}:${id}` way. Real table
     ids (globally unique per table + kind prefix), not per-matter JSON ints,
     so the 110 §6a collision class does not arise; this view never switches
     matter either. Read-only: the agenda is not an editing surface. */
  const [openNotes, setOpenNotes] = useState(() => new Set());
  const toggleNotes = (key: string) =>
    setOpenNotes((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const refreshAgenda = async () => {
    try {
      const fresh = await getAgenda();
      sAg(fresh);
      /* 210 §1 — the `lx:ag` copy written here had one reader, the boot's
         fallback, which is gone (src/lib/listLoad.ts): nothing reads it. */
    } catch (e: any) {
      toast(
        t("agenda.toast.reload_failed", {
          msg: e.message || t("common.unknown"),
        }),
      );
    }
  };
  // Phase M+2b's edit-others gate (`_canEditOthersTasks`, admin only) left with
  // the task line's pencil, delete and archive (216 addendum 4 §8).
  const _perms = usePermissions();
  // Edit gate for an agenda event: admin always; linked events use the
  // caseView.agenda.edit permission scoped to the linked case/cc; unlinked
  // events are admin-only (no per-row scope to gate on).
  const _canEditEv = (ev: any) => {
    if (_perms.viewer?.isAdmin) return true;
    if (ev.caId) return _perms.can("caseView.agenda.edit", { caseId: ev.caId });
    if (ev.ccId) return _perms.can("caseView.agenda.edit", { ccId: ev.ccId });
    return false;
  };
  // Navigation gate: only navigate into the case if the viewer has any
  // case-internal section beyond agenda. Reception has only agenda.view, so
  // navigating into the case would land her on an empty CaseView. Sentinel:
  // caseView.parties (always-true for Tier A-C, false for Reception/Secretary).
  const _canNavCase = (cs: any, ccx: any) => {
    if (_perms.viewer?.isAdmin) return true;
    if (ccx) return _perms.can("collectiveCase.clientList.view", { ccId: ccx.id });
    if (cs) return _perms.can("caseView.parties.view", { caseId: cs.id });
    return false;
  };
  /* Phase 30 Stage-3 (C-1) — the C85 guard, which every other tap-through
     surface (bell, personal messages, compact column, Dashboard) already
     runs: a click on a row whose case the viewer cannot open shows the
     refusal toast instead of a silent nothing. Two refusal flavours exist
     here: case not in the viewer's scoped lists (orphan row via delegation /
     firm-wide REU), and case loaded but the _canNavCase sentinel says a jump
     would land on an empty CaseView (Reception persona). Same toast for
     both — same key as App.jsx's tryNavCase. */
  const _navCase = tryNavCase || ((kind: any, id: any) => (nav(kind, id), true));
  const _navRefused = () =>
    toast(
      t("nav.case_no_access", {
        fallback:
          "You don't have access to this casefile — ask the Responsible Partner",
      }),
      "warning",
    );
  useEffect(() => {
    let id1, id2: any;
    id1 = requestAnimationFrame(() => {
      id2 = requestAnimationFrame(() => {
        if (agViewMode === "calendar") {
          const el = document.getElementById("ag-cal-current");
          if (el) el.scrollIntoView({ block: "start", behavior: "auto" });
        }
      });
    });
    return () => {
      if (id1) cancelAnimationFrame(id1);
      if (id2) cancelAnimationFrame(id2);
    };
  }, [agViewMode]);
  // Brief 69 §3 — the calendar's "land on the current month" scroll, MOVED here
  // out of the JSX, where it was written `{setTimeout(…, 0) && false}`: the
  // `&& false` suppressed the render and the timeout did the work. It is not
  // dead code and was ruled a MOVE rather than a deletion (2026-08-09) for
  // exactly that reason. The shape is preserved rather than improved: the same
  // timeout, the same `data-scrolled` latch, the same body. The latch is what
  // makes it fire once, and it fires once PER VISIT because it lives on the
  // element — `#ag-cal-current` is created fresh every time the calendar branch
  // mounts, so a new visit is a new element and an unset latch. This effect
  // carries no dependency array, so it runs after every commit, which is a
  // superset of the renders the JSX expression ran on (that one ran only when
  // the calendar branch rendered, which the guard below reproduces). It cannot
  // therefore fire fewer times than before, and the extra runs are all no-ops:
  // in list mode there is no element, and after the first scroll the latch is
  // set. NOT consolidated with the effect above, which is a different mechanism
  // (double-rAF, keyed on the mode, no latch) added four days later and living
  // alongside this one ever since — merging the three scroll paths this view
  // has is an improvement, and improvements are their own ruling.
  useEffect(() => {
    if (agViewMode !== "calendar") return;
    const id = setTimeout(() => {
      const el = document.getElementById("ag-cal-current");
      if (el && !el.dataset.scrolled) {
        el.dataset.scrolled = "1";
        el.scrollIntoView({ block: "start", behavior: "auto" });
      }
    }, 0);
    return () => clearTimeout(id);
  });
  const curCo = currentUser ? co.find((x: any) => x.id === currentUser) : null;
  /* ── 174 §6.6 — THE INVERSE OF A GHOST, REPAIRED ───────────────────────────
     What stood here read `_perms.viewer?.role?.firmWideVisibility === true`.
     THAT FIELD WAS DROPPED IN PHASE 24 (`schema.prisma:2493`: "firmWideVisibility
     DROPPED. Cross-case visibility is no longer a per-role flag; it is governed
     per-SURFACE by FirmSettings.surfaceScopes … resolved in permissions.js").
     `undefined === true` is ALWAYS FALSE, so for eighteen months this view took
     the restrictive branch for every non-admin whatever the firm's scope said —
     a setting honoured on the server and ignored on the screen.

     AND THE REPAIR IS TO DELETE THE FILTER, not to point it at a scope. MEASURED:
     the server has ALREADY applied the scope to these very rows.
     `routes/agenda.js:504` `filterAgendaEvents` runs every row through
     `buildRowGate(viewer, 'caseView.agenda.view', { caselessFirmWide, typeFirmWide })`,
     and `lib/rowScope.js:98` resolves that gate's surface and calls
     `getVisibleCaseIds(viewer, surface)` — which is `FirmSettings.surfaceScopes`,
     the exact mechanism Phase 24 named. So `ag` already holds precisely the
     events this viewer may see; a second assignee test on top of it hid rows the
     server had already decided for, on a rule that does not exist.

     `agMy` is NOT this filter and stays: that is the user's own "my events"
     toggle, an explicit narrowing they asked for. What goes is the involuntary
     one — and with it the local `isAdmin`, whose only reader it was. Every
     other admin question in this file already asks `_perms.viewer?.isAdmin`
     directly. */
  const baseAg0 = [...ag]
    .filter((a: any) => af === "all" || (af === "ca" ? a.caId : !a.caId))
    .filter((a: any) => {
      /* Brief 86 §2f — one collaborator's workload. It sits INSIDE agMy's
         filter and before the visibility term, so "my" still wins (the two are
         the same question asked about two people) and a non-admin still cannot
         widen their own view with it. */
      if (agCoFilt && !(agMy && currentUser))
        return (a.assignedCollaborators || []).includes(+agCoFilt);
      if (agMy && currentUser)
        return (a.assignedCollaborators || []).includes(currentUser);
      /* 174 §6.6 — the involuntary assignee filter that stood here is gone:
         the server already scoped these rows (see the note above). Everything
         `ag` carries is something this viewer may see. */
      return true;
    })
    .filter((a: any) => {
      const ql = (q || "").toLowerCase();
      if (!ql) return true;
      if ((a.title || "").toLowerCase().includes(ql)) return true;
      const _cs = a.caId ? ca.find((c: any) => c.id === a.caId) : null;
      const _cc = a.ccId ? (cc || []).find((c: any) => c.id === a.ccId) : null;
      const linked = _cs || _cc;
      if (linked) {
        if ((linked.ref || "").toLowerCase().includes(ql)) return true;
        if ((linked.title || "").toLowerCase().includes(ql)) return true;
      }
      return false;
    });
  // 58 §4 — one predicate for four surfaces (src/lib/eventTypeFilter.ts). The
  // set-of-hidden-codes it replaces said the same thing and said it only here.
  const baseAg = baseAg0.filter((a: any) => eventTypeVisible(agTypeFilt, a));
  const now4 = new Date();
  const todayStrAg2 = now4.toISOString().slice(0, 10);
  const dow4 = now4.getDay();
  const monD4 = new Date(now4);
  monD4.setDate(now4.getDate() - (dow4 === 0 ? 6 : dow4 - 1));
  const sunD4 = new Date(monD4);
  sunD4.setDate(monD4.getDate() + 6);
  const mon1D4 = new Date(now4.getFullYear(), now4.getMonth(), 1);
  const monLastD4 = new Date(now4.getFullYear(), now4.getMonth() + 1, 0);
  const d30 = new Date(now4);
  d30.setDate(d30.getDate() + 30);
  const d15 = new Date(now4);
  d15.setDate(d15.getDate() + 15);
  const drStart =
    agDateRange === "week"
      ? monD4.toISOString().slice(0, 10)
      : agDateRange === "month"
        ? mon1D4.toISOString().slice(0, 10)
        : agDateRange === "30d"
          ? todayStrAg2
          : agDateRange === "15d"
            ? todayStrAg2
            : null;
  const drEnd =
    agDateRange === "week"
      ? sunD4.toISOString().slice(0, 10)
      : agDateRange === "month"
        ? monLastD4.toISOString().slice(0, 10)
        : agDateRange === "30d"
          ? d30.toISOString().slice(0, 10)
          : agDateRange === "15d"
            ? d15.toISOString().slice(0, 10)
            : null;
  const filtByDate = drStart
    ? baseAg.filter(
        (a) =>
          a.date &&
          a.date.slice(0, 10) >= drStart &&
          a.date.slice(0, 10) <= drEnd!,
      )
    : baseAg;
  const _todayD = new Date();
  const _monD = new Date(_todayD);
  _monD.setDate(_todayD.getDate() - ((_todayD.getDay() + 6) % 7));
  const monStrW = _monD.toISOString().slice(0, 10);
  const hiddenAgCount = filtByDate.filter(
    (a) => a.date && a.date.slice(0, 10) < monStrW,
  ).length;
  const sorted = filtByDate
    .filter((a: any) => showEarlier || !a.date || a.date.slice(0, 10) >= monStrW)
    .sort((a: any, b: any) => (new Date(a.date) as any) - (new Date(b.date) as any));
  /* ── 216 addendum 4 §8 (2) [RULED 79] — A TASK SHOWS ON ITS OWN DUE DATE AND
     ON EACH OF ITS SUBTASKS'. Until now a task was placed by its own `dueDate`
     only, and a task with no date of its own and dated subtasks was not shown
     at all. A LINE is (task, date, the live subtasks due that date): the
     collaborator and « mine » filters and the search stay the task's; the
     range and « show earlier » apply to the line's date; the showing count
     counts distinct tasks. The month calendar reads no task (§8.5). */
  const _liveSubs = (tsk: any): any[] =>
    (Array.isArray(tsk.subtasks) ? tsk.subtasks : []).filter((st: any) => st && !st.archived);
  const _dayOf = (d: unknown): string => (d ? String(d).slice(0, 10) : "");
  /* Brief 86 §2f — the ruling says the picker filters this view's events AND
     tasks; this is the tasks half, on the same precedence — asked of ONE
     person's id, so a line can ask it of the subtask's assignee or the task's. */
  const _coPass = (coId: unknown): boolean => {
    if (agCoFilt && !(agMy && currentUser)) return coId === +agCoFilt;
    if (agMy && currentUser) return coId === currentUser;
    return true;
  };
  const _ql = (q || "").toLowerCase();
  /* 166 §E — THE SEARCH MISSED EVERY CC-BOUND TASK, for the same reason the
     row did: it resolved `caseId` alone, so a task on a collective case
     matched only on its own title and never on its matter's name or ref.
     Same fix, same shape as the event renderer's pair. */
  const _tkMatches = (tsk: any): boolean => {
    if (!_ql) return true;
    if ((tsk.title || "").toLowerCase().includes(_ql)) return true;
    const _cs =
      (tsk.caseId ? ca.find((c: any) => c.id === tsk.caseId) : null) ||
      (tsk.ccId ? (cc || []).find((c: { id: number }) => c.id === tsk.ccId) : null);
    return !!_cs && ((_cs.ref || "").toLowerCase().includes(_ql) || (_cs.title || "").toLowerCase().includes(_ql));
  };
  const _stMatches = (st: any): boolean => !_ql || (resolveSubtaskText(st) || "").toLowerCase().includes(_ql);
  const baseTk = (tk || []).filter((t: any) => t.status !== "cancelled" && t.status !== "archived");
  /* 216 addendum 6 §10 (4) [RULED 82] — A SUBTASK'S LINE ANSWERS « MES », THE
     COLLABORATOR FILTER AND THE SEARCH BY THE SUBTASK: its assignee, else its
     task's; the search matches the subtask's text or its task's. A task's own
     line keeps the task's rule. Until now the three read the task on every
     line, so a subtask assigned to the viewer under another's task never
     showed under « Mes » (the deploy note §6 of addenda 3–5). A line shows
     when it is the task's own date and the task passes, or when a subtask due
     that day passes by its own rule; the subtasks it carries are those. */
  const tkLines: TkLine[] = [];
  baseTk.forEach((tsk: any) => {
    const own = _dayOf(tsk.dueDate);
    const subs = _liveSubs(tsk);
    const dates = new Set<string>([own, ...subs.map((st: any) => _dayOf(st.dueDate))].filter(Boolean));
    const taskPasses = _coPass(tsk.assignedCoId) && _tkMatches(tsk);
    dates.forEach((date) => {
      if (drStart && (date < drStart || date > drEnd!)) return;
      if (!showEarlier && date < monStrW) return;
      const shown = subs.filter(
        (st: any) =>
          _dayOf(st.dueDate) === date &&
          _coPass(st.assignedCoId ?? tsk.assignedCoId) &&
          (_tkMatches(tsk) || _stMatches(st)),
      );
      const ownLine = date === own && taskPasses;
      if (!ownLine && shown.length === 0) return;
      tkLines.push({ tsk, date, subs: shown, own: date === own });
    });
  });
  tkLines.sort((a, b) => a.date.localeCompare(b.date) || Number(a.tsk.id) - Number(b.tsk.id));
  const sortedTkCount = new Set(tkLines.map((l) => l.tsk.id)).size;
  const todayStrAg = todayStrAg2;
  const _monthFmt = new Intl.DateTimeFormat(locale, { month: "long" });
  const _monthName = (m: any) => {
    try {
      return _monthFmt.format(new Date(2000, m, 1));
    } catch {
      return String(m + 1);
    }
  };
  const _shortDayFmt = new Intl.DateTimeFormat(locale, { weekday: "short" });
  const _shortDayName = (dow: any) => {
    try {
      const d = new Date(2024, 0, 1);
      const cur = d.getDay() || 7;
      d.setDate(d.getDate() + (dow - cur));
      return _shortDayFmt.format(d);
    } catch {
      return "";
    }
  };

  /* ── 216 addendum 4 §8 (2) [RULED 79] — THE AGENDA PAGE'S TWO LINES ──────────
     Laurent, 14/10: « the purpose of agenda view is to offer a less cluttered
     overview, fitting its purpose ». Each line is ONE GRID whose two rows share
     their columns, and every line of a list has the same columns [ADVISOR]: the
     fixed tracks are constants — the type tag's box (the mirror's width, 139
     §1), the people area (BADGE_AREA_W), one slot per icon, the days counter —
     and the two texts split the rest in a fixed proportion (1fr : 2fr), so the
     hour sits under the type tag and the place under the description, line
     after line. The census of what each piece of the old lines became is
     docs/216-deploy-note.md §7.5; what Laurent's list does not name has left
     the line [ADVISOR — the deploy note §4]: the timer (his words), edit,
     delete, archive, the .ics download, the subtask counters and drawers, the
     priority letter, the note excerpt. A click on the line still opens the item
     in its matter; the icons and the status pills do what they do today. No
     time, no timers (116 §3 is replaced here by ruling 79). */
  const renderEvRow = (ev: any) => {
    const __dimPast = ev.date && ev.date.slice(0, 10) < todayStrAg;
    const _cs = ev.caId ? ca.find((c: any) => c.id === ev.caId) : null;
    const _cc = ev.ccId ? (cc || []).find((c: any) => c.id === ev.ccId) : null;
    const _linked = _cs || _cc;
    const _navOk = _linked && _canNavCase(_cs, _cc);
    // Phase 30 Stage-3 (C-1) — case-linked rows are clickable even when the
    // jump will be refused: the click shows the C85 toast, never nothing.
    const _clickable = _navOk || ev.caId != null || ev.ccId != null;
    /* 111 §3 — the note, in reach without leaving the list. */
    const _noteKey = "ev:" + ev.id;
    const _noteOpen = !!ev.notes && openNotes.has(_noteKey);
    /* 216 §1 [RULED 70] → §8 [RULED 79]: the hover names the matter, its ref
       and the event's own title; `_evOwn` is "" when the title only repeats the
       matter (not shown twice). The ref itself left the line for the hover. */
    const { own: _evOwn, tip: _rowTip } = agendaRowTitles(ev, _linked);
    const _caseName: string = _linked ? _linked.title || "" : "";
    const _caseTip: string = _linked ? [_linked.title, _linked.ref].filter(Boolean).join(" — ") : "";
    // « Description »: the event's own title; an event with no visible matter shows its title alone, as before
    const _desc: string = _linked ? _evOwn : ev.title || "";
    const _navToCase = () => {
      queueScrollToEv(ev.id);
      if (_cc) nav("cc", _cc.id);
      else nav("ca", _cs.id);
    };
    const _onRowClick = () => {
      if (_navOk) _navToCase();
      else if (ev.caId != null || ev.ccId != null) _navRefused();
    };
    const evDate = ev.date ? ev.date.slice(0, 10) : "";
    const diff: any = dayDiff(evDate, todayStrAg);
    const label = dayCountdownLabel(diff, t);
    // C72 — the event's documents (the list; archived rows do not count); the icon opens the first (the readonly pattern)
    const _evDocs = (Array.isArray(ev.docs) ? ev.docs : []).filter((d: any) => d && !d.archived);
    const _place = placeOf(ev);
    const _openDoc = (e: React.MouseEvent) => {
      e.stopPropagation();
      /* 126 §9 — an e-mail document resolves on demand; the fork is the caller's. */
      if (isEmailDocUrl(_evDocs[0].url)) {
        void openEmailDocHref(_evDocs[0].url, t);
        return;
      }
      openDocHref(_evDocs[0].url);
    };
    return (
      <div
        key={ev.id}
        className="event-item"
        data-lnk={"event-" + ev.id}
        data-ag-line="event"
        title={_rowTip}
        style={{
          /* 216 addendum 6 §10 (1) [RULED 81] — the card IS the line: a subgrid on the day's columns (agList), the
             two rows its own, the note's reveal a third; `.event-title`'s face (11,5 px, 500) on the card itself */
          ...agLine(2),
          rowGap: 4,
          fontSize: FS.s11_5,
          fontWeight: 500,
          ...(__dimPast ? { opacity: 0.5 } : {}),
          cursor: _clickable ? "pointer" : "default",
          overflow: "hidden",
        }}
        onClick={_onRowClick}
      >
        {/* row 1 — the type tag · the people · the case name · the description · document · note · link · the days */}
        <span style={{ gridColumn: 1, gridRow: 1, display: "inline-flex" }}>
          <TypeTagBox mirror={_typeMirror}>{etTag(ev.type)}</TypeTagBox>
        </span>
        {/* BRIEF 95 §2c — `coAvs` resolves the matter's responsible partner
            among the attendees (the principal first, the viewer ringed when
            the work is his); its panel names everyone. */}
        {/* §11 (1) [RULED 84] — the people area spans the two slots a task's Eisenhower badge and assignee take */}
        <span style={{ gridColumn: "2 / 4", gridRow: 1, display: "inline-flex", alignItems: "center", minWidth: 0 }}>
          {coAvs(ev.assignedCollaborators, ev)}
        </span>
        <span style={{ gridColumn: 4, gridRow: 1, ...AG_ELLIPSIS }}>
          {_caseName && (
            <span
              className="link"
              data-ag-case=""
              title={_caseTip}
              style={{ fontWeight: 600, cursor: _navOk ? "pointer" : "default" }}
              onClick={
                _navOk
                  ? (e) => {
                      e.stopPropagation();
                      _navToCase();
                    }
                  : undefined
              }
            >
              {_caseName}
            </span>
          )}
        </span>
        <span data-ag-ev-title="" title={_desc || undefined} style={{ gridColumn: 5, gridRow: 1, ...AG_ELLIPSIS }}>
          {_desc}
        </span>
        <span style={{ gridColumn: 6, gridRow: 1, ...AG_CENTER }}>
          {_evDocs.length > 0 && (
            <button
              type="button"
              data-ag-doc=""
              style={{ ...AG_ICON_BTN, color: DRAWER_ICON_HAS, cursor: "pointer" }}
              title={_evDocs[0].label || _evDocs[0].url}
              onClick={_openDoc}
            >
              <File size={11} />
            </button>
          )}
        </span>
        {/* 111 §3 — the note icon, 110 §1d's rules: always visible, muted when
            empty, filled ink when a note exists; the tooltip carries the note,
            a click toggles the quiet one-line reveal below — read-only. */}
        <span style={{ gridColumn: 7, gridRow: 1, ...AG_CENTER }}>
          <button
            type="button"
            data-ag-note=""
            style={{
              ...AG_ICON_BTN,
              color: ev.notes ? DRAWER_ICON_HAS : DRAWER_ICON_EMPTY,
              cursor: ev.notes ? "pointer" : "default",
            }}
            title={ev.notes || undefined}
            onClick={(e) => {
              e.stopPropagation();
              if (ev.notes) toggleNotes(_noteKey);
            }}
          >
            <NotebookPen size={11} />
          </button>
        </span>
        {/* 111 §5c — the Link icon (the matter row is _linked); its slot stays when the item has none */}
        <span style={{ gridColumn: 8, gridRow: 1, ...AG_CENTER }}>
          <LinkIcon matterRow={_linked} item={{ k: "event", id: ev.id }} />
        </span>
        {/* BRIEF 136 §3(3) — the counter, or the status glyph that replaces
            it; THE COLOUR LADDER STAYS THIS VIEW'S. §8: at the right,
            spanning both rows, centred — its right padding is the card's.
            §10 (1): the column is the list's, as wide as its widest counter. */}
        <span data-ag-days="" style={agDaysCell(2)}>
          <EventDayMark
            label={label}
            color={agDayColor(diff)}
            isPast={diff < 0}
            status={eventStatusOf(ev)}
            statusEntries={evStatusEntries}
          />
        </span>
        {/* row 2 — the hour under the type tag, then the join button and the
            place RIGHT AFTER IT, ellipsised (§10 (1) [RULED 81]; BRIEF 124 §4:
            the room wins; §8: up to its first comma, the whole on hover) */}
        <span
          data-ag-row2=""
          style={{
            gridColumn: "1 / -2",
            gridRow: 2,
            display: "flex",
            alignItems: "center",
            gap: AG_GAP,
            color: "var(--tm)",
            fontSize: FS.s10_5,
            fontWeight: 400,
            minWidth: 0,
          }}
        >
          <span data-ag-hour="" style={{ flexShrink: 0 }}>
            {ev.time || ""}
          </span>
          {/* Phase 31 (C4) — Join affordance on rows with a meeting URL. */}
          <JoinBtn {...({} as any)} url={ev.joinUrl} stub={!!ev.joinUrlStub} />
          {_place.shown && (
            <span data-ag-place="" title={_place.whole ?? undefined} style={AG_ELLIPSIS}>
              {_place.shown}
            </span>
          )}
        </span>
        {/* 111 §3 — the reveal: one quiet line inside the card, read-only. */}
        {_noteOpen && (
          <div
            title={ev.notes}
            style={{
              gridColumn: "1 / -1",
              gridRow: 3,
              fontSize: FS.s11,
              fontWeight: 400,
              color: "var(--ts)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {ev.notes}
          </div>
        )}
      </div>
    );
  };

  // The task line — one per (task, date): the main task on row 1, the three icons
  // under their columns on row 2, and the subtasks due that day one row each.
  const renderTkRow = (line: TkLine) => {
    const tsk = line.tsk;
    /* 166 §E — an agenda task names its matter of either kind (ruled 2026-09-04). */
    const cs2 =
      (tsk.caseId ? ca.find((c: any) => c.id === tsk.caseId) : null) ||
      (tsk.ccId ? (cc || []).find((c: { id: number }) => c.id === tsk.ccId) : null);
    const cs2Kind: "ca" | "cc" = tsk.caseId != null ? "ca" : "cc";
    const tkt = tkTypes ? tkTypes.find((x: any) => x.code === tsk.type) : null;
    const isDone = tsk.status === "completed";
    const isArch = tsk.status === "archived";
    const _ownDate = _dayOf(tsk.dueDate);
    const isOvd = !isDone && !!_ownDate && _ownDate < todayStrAg;
    const asn = tsk.assignedCoId ? co.find((x: any) => x.id === tsk.assignedCoId) : null;
    /* 111 §3 — the note, in reach without leaving the list. */
    const _noteKey = "tk:" + tsk.id;
    const _noteOpen = !!tsk.notes && openNotes.has(_noteKey);
    /* C65 / 166 §E — an off-team delegatee resolves no matter row; the task
       itself carries the title (display-only: no nav). */
    const _ct: string | null = cs2 ? cs2.title : tsk.caseTitle || tsk.ccTitle || null;
    const _caseTip: string = cs2 ? [cs2.title, cs2.ref].filter(Boolean).join(" — ") : _ct || "";
    const _rowTip = [_caseTip, tsk.title].filter(Boolean).join(" — ");
    // the days counter counts to the LINE's date [ADVISOR]
    const diff: any = dayDiff(line.date, todayStrAg);
    const label = dayCountdownLabel(diff, t);
    const rows = 1 + Math.max(1, line.subs.length);
    /* Round 13 (C72) — the icon reads the DOCUMENT LIST; archived docs do not count. */
    const _docs = (Array.isArray(tsk.docs) ? tsk.docs : []).filter((d: any) => d && !d.archived);
    const _navTk = () => {
      queueScrollToTk(tsk.id);
      nav(cs2Kind, cs2.id);
    };
    const _openDoc = (e: React.MouseEvent) => {
      e.stopPropagation();
      /* 126 §9 — an e-mail document resolves on demand (the dictionary's `t`:
         the task was named `t` here before and shadowed it). */
      if (isEmailDocUrl(_docs[0].url)) {
        void openEmailDocHref(_docs[0].url, t);
        return;
      }
      openDocHref(_docs[0].url);
    };
    return (
      <Fragment key={`${tsk.id}:${line.date}`}>
        <div
          className="tkRow"
          data-ag-line="task"
          data-lnk={"task-" + tsk.id}
          data-ag-date={line.date}
          title={_rowTip}
          style={{
            /* 216 addendum 6 §10 (1) [RULED 81] — a subgrid on the day's columns (agList); its rows its own */
            ...agLine(rows),
            rowGap: 3,
            /* BRIEF 102 §5d — the rows get air: 7px is the event card's own.
               216 addendum 7 §11 (1) [RULED 84] — and the days counter keeps an event line's right inset: the
               card's 9 px padding and its 1 px border, 10 px (Laurent, 16/10: « counter in tasks has no padding,
               seems cut off »); a subgrid's padding insets its edge track, so the counter ends 10 px short of
               the line's edge while the column stays the list's. */
            padding: "7px 10px 7px 0",
            borderBottom: "1px solid rgba(255,255,255,.04)",
            opacity: isDone || isArch ? 0.55 : 1,
            fontSize: FS.s11_5,
            cursor: cs2 || tsk.caseId != null || tsk.ccId != null ? "pointer" : "default",
            overflow: "hidden",
            whiteSpace: "nowrap",
          }}
          onClick={() => {
            // Phase 23 (Stage 3) — uniform rule: row-click jumps to the linked case.
            if (cs2) _navTk();
            else if (tsk.caseId != null || tsk.ccId != null) {
              /* C-1 — same guard as Dashboard's task rows (C85). */
              if (_navCase(tsk.caseId != null ? "ca" : "cc", tsk.caseId != null ? tsk.caseId : tsk.ccId))
                queueScrollToTk(tsk.id);
            }
          }}
        >
          {/* row 1 — the type tag · Eisenhower · the assignee · the case name · the description · document · note ·
              the status · the days (§10 (1) [RULED 81]: the two icons follow the description; they stood on row 2) */}
          <span style={{ gridColumn: 1, gridRow: 1, display: "inline-flex" }}>
            {/* 216 addendum 7 §11 (1) [RULED 84] — the tag in the events' box (139 §1): the mirror of the firm's
                longest task code makes the column that code's width, so every code shows whole (the fixed 36 px
                column cut « ADMIN » to « ADM… » on Laurent's screen) */}
            <TypeTagBox mirror={_tkTypeMirror}>
              {tkt ? (
                /* 204 §7.1 — the chip's one definition (TypeTagBox's TYPE_CHIP) */
                <span title={tkt.desc} style={TYPE_CHIP}>
                  {tsk.type}
                </span>
              ) : (
                <span style={{ width: 1 }} />
              )}
            </TypeTagBox>
          </span>
          <span style={{ gridColumn: 2, gridRow: 1, ...AG_CENTER }} title={eisenLabel(eisenQuadrant(tsk))}>
            <EisenBadge q={eisenQuadrant(tsk)} size={11} />
          </span>
          <span style={{ gridColumn: 3, gridRow: 1, ...AG_CENTER }}>
            {asn ? (
              <>
                {/* §5d — the event line's avatar size (`coAvs`), 90 §3c's one size */}
                <InitialsBadge {...({} as any)} person={asn} isMe={asn.id === currentUser} size={22} fontSize={9} />
                {/* 75 §3 — a task's only person, archived: the earmark. */}
                {isArchivedCo(asn) && <ArchivedHoleBadge size={22} fontSize={9} style={{ marginLeft: 3 }} />}
              </>
            ) : (
              <span style={{ color: "var(--tm)", fontSize: FS.s9 }}>—</span>
            )}
          </span>
          <span style={{ gridColumn: 4, gridRow: 1, ...AG_ELLIPSIS, padding: "0 4px" }}>
            {_ct && (
              <span
                className="link"
                data-ag-case=""
                title={_caseTip}
                style={{ fontWeight: 500, cursor: cs2 ? "pointer" : undefined }}
                onClick={
                  cs2
                    ? (e) => {
                        e.stopPropagation();
                        _navTk();
                      }
                    : undefined
                }
              >
                {_ct}
              </span>
            )}
          </span>
          <span
            data-ag-tk-title=""
            title={tsk.title}
            style={{
              gridColumn: 5,
              gridRow: 1,
              ...AG_ELLIPSIS,
              padding: "0 4px",
              fontWeight: 500,
              color: isDone ? "var(--gn)" : isOvd ? "var(--rd)" : undefined,
              textDecoration: isDone ? "line-through" : "none",
            }}
          >
            {tsk.title}
          </span>
          <span style={{ gridColumn: 6, gridRow: 1, ...AG_CENTER }}>
            {_docs.length > 0 && (
              <button
                type="button"
                data-ag-doc=""
                style={{ ...AG_ICON_BTN, color: DRAWER_ICON_HAS, cursor: "pointer" }}
                /* BRIEF 107 §3a — the link names its target; `(+N)` kept in the title's spirit by the count's absence */
                title={_docs[0].label || _docs[0].url}
                onClick={_openDoc}
              >
                <File size={11} />
              </button>
            )}
          </span>
          <span style={{ gridColumn: 7, gridRow: 1, ...AG_CENTER }}>
            <button
              type="button"
              data-ag-note=""
              style={{
                ...AG_ICON_BTN,
                color: tsk.notes ? DRAWER_ICON_HAS : DRAWER_ICON_EMPTY,
                cursor: tsk.notes ? "pointer" : "default",
              }}
              title={tsk.notes || undefined}
              onClick={(e) => {
                e.stopPropagation();
                if (tsk.notes) toggleNotes(_noteKey);
              }}
            >
              <NotebookPen size={11} />
            </button>
          </span>
          {/* the status pill, display-only (the readonly pattern) */}
          <span style={{ gridColumn: 8, gridRow: 1, display: "inline-flex", alignItems: "center", marginRight: 3 }}>
            {TkStatusTag(tsk, true)}
          </span>
          <span data-ag-days="" style={agDaysCell(rows)}>
            {agDays(label, agDayColor(diff), diff < 0)}
          </span>
          {/* row 2 — the link icon under the type tag (the MAIN task's [ADVISOR]); the subtasks due that day under
              the description, each with its status pill under the task's */}
          <span style={{ gridColumn: 1, gridRow: 2, ...AG_CENTER }}>
            <LinkIcon matterRow={cs2} item={{ k: "task", id: tsk.id }} />
          </span>
          {line.subs.map((st: any, i: number) => {
            const _text = resolveSubtaskText(st) || t("agenda.untitled_subtask", { fallback: "(untitled)" });
            /* 118 §2 — a subtask's status: its column, else `done` (NULL means the backfill did not reach it) */
            const _stStatus = st.status || (st.done ? "completed" : "todo");
            return (
              <Fragment key={st.id ?? i}>
                <span
                  data-ag-subtask=""
                  title={_text}
                  style={{
                    gridColumn: 5,
                    gridRow: 2 + i,
                    ...AG_ELLIPSIS,
                    padding: "0 4px",
                    fontSize: FS.s10_5,
                    color: st.done ? "var(--tm)" : "var(--ts)",
                    textDecoration: st.done ? "line-through" : "none",
                  }}
                >
                  {_text}
                </span>
                <span style={{ gridColumn: 8, gridRow: 2 + i, display: "inline-flex", alignItems: "center", marginRight: 3 }}>
                  {TkStatusTag({ ...st, status: _stStatus, caseId: tsk.caseId, ccId: tsk.ccId }, true)}
                </span>
              </Fragment>
            );
          })}
        </div>
        {/* 111 §3 — the reveal: one quiet line under the line, read-only (a row of the list's grid, all columns). */}
        {_noteOpen && (
          <div
            title={tsk.notes}
            style={{
              gridColumn: "1 / -1",
              fontSize: FS.s11,
              color: "var(--ts)",
              padding: "2px 8px 6px 36px",
              borderBottom: "1px solid rgba(255,255,255,.04)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {tsk.notes}
          </div>
        )}
      </Fragment>
    );
  };

  // 58 §4 — the chip set, its order, its colours and the toggle all moved to
  // src/lib/eventTypeFilter.ts, and the rendering to <EventTypeChips>. What
  // stood here derived the chips from `ag` itself, so a type added in Settings
  // had no chip until an event used it. Four surfaces mount the component now.
  const setDateRange = (r: any) => {
    setAgDateRange(r);
    if (agViewMode !== "list") {
      setAgViewMode("list");
    }
  };

  // Group events and tasks by date.
  // 109 §4b — tasks key by the CALENDAR DAY, like events: a `dueDate` carrying
  // a time suffix minted a second key for the same day, so "22 AUG" rendered
  // twice — one block holding only the events (sliced key), one only the tasks
  // (raw key). Normalised in all three places (`allDates`, `tkByDate`; the
  // `dayTks` lookup follows for free).
  const allDates = new Set<any>();
  sorted.forEach((ev) => {
    if (ev.date) allDates.add(ev.date.slice(0, 10));
  });
  // 216 addendum 4 §8 — a task's LINES key the days (one per date it shows on)
  tkLines.forEach((l) => allDates.add(l.date));
  const dateList = [...allDates].sort();
  const evByDate: Record<string, any> = {};
  sorted.forEach((ev) => {
    const d = ev.date?.slice(0, 10);
    if (d) (evByDate[d] = evByDate[d] || []).push(ev);
  });
  const tkByDate: Record<string, TkLine[]> = {};
  tkLines.forEach((l) => {
    (tkByDate[l.date] = tkByDate[l.date] || []).push(l);
  });

  return (
    <>
      <div
        className="tkHdr"
        style={{
          position: "sticky",
          /* 182 D.5 (7) — the literal 14 WAS `.content`'s top padding
             (`--content-pad-t`, App.css:53): the header pulls itself up over
             that padding (margin) and pins at its negative (top) so it rests
             flush with the scrollport's visible edge — 177 §1b's mechanism,
             which `.teTable-page thead th` (App.css) already spells with the
             token. Measured identical before and after
             (`drive-177-sticky-header.js --site=agenda`: pin box `.content`,
             gap 0, rows in the gap 0). The 20 is `.content`'s side padding
             and stays a literal, as it is in App.css itself. */
          top: "calc(-1 * var(--content-pad-t))",
          zIndex: Z.inlineMenu,
          background: "var(--bs)",
          margin: "calc(-1 * var(--content-pad-t)) -20px 0",
          padding: "12px 20px 6px",
          borderBottom: "1px solid var(--br)",
        }}
      >
        <div
          style={{
            display: "flex",
            gap: 4,
            marginBottom: 4,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <div className="search-box" style={{ width: 240 }}>
            <Search size={12} style={{ color: "var(--tm)", flexShrink: 0 }} />
            <input
              placeholder={t("common.search_placeholder")}
              value={q || ""}
              onChange={(e) => sQ(e.target.value)}
            />
          </div>
          {currentUser && (
            <MyAllToggle
              {...({} as any)}
              my={agMy}
              onMy={() => sAgMy(true)}
              onAll={() => sAgMy(false)}
              counts={{
                my: ag.filter((a: any) =>
                  (a.assignedCollaborators || []).includes(currentUser),
                ).length,
                all: ag.length,
              }}
            />
          )}
          {/* Brief 86 §2f — ONE dropdown, an admin's or an RP's, filtering this
              view's events AND tasks to a single collaborator so a workload can
              be read. Visibility follows §2a-2's shape: admin, or a responsible
              partner of some matter — a collaborator who cannot see the whole
              team's work is not offered a control for reading it.

              86 mounted the tree's AssigneePicker here (widened with
              `emptyLabel` — that prop and the [MEASURED] no-other-candidate
              census live in AssigneePicker.tsx). 109 §4c supersedes the
              COMPONENT, not the decision: the same filter — same gate, same
              `agCoFilt` state, same collaborator pool canAssignAll offered
              (`choosableCo`, keeping the archived CURRENT selection so a stored
              filter never silently re-points) — as the app's autocomplete
              single-select. It types-ahead like the modals' pickers, its popup
              follows the app theme (a native <select>'s follows the OS, §6's
              report), and the trigger is its own label's width instead of the
              widest option's. */}
          {(!!_perms.viewer?.isAdmin || !!_perms.viewer?.isRpOfAnyCase) && (
            /* 110 §4a — the id scopes an App.css override that releases the
               .field-input --field-h floor so this trigger equals the
               .search-box beside it (measured 32.3 vs 26.6 before). */
            <Combobox
              options={choosableCo(co, agCoFilt ? [agCoFilt] : null).map(
                (c: any) => ({ value: String(c.id), label: pname(c) }),
              )}
              value={agCoFilt ? String(agCoFilt) : ""}
              placeholder={t("agenda.filter.all_collaborators", {
                fallback: "— All collaborators —",
              })}
              onChange={(v: any) => sAgCoFilt(v ? +v : null)}
              id="agCoFilt"
              style={{ width: "fit-content", minWidth: 150, fontSize: FS.s10 }}
            />
          )}
          <div
            style={{
              margin: "0 auto",
              display: "flex",
              border: "1px solid var(--br)",
              borderRadius: RAD.r5,
              overflow: "hidden",
            }}
          >
            <button
              style={{
                padding: "3px 7px",
                background: agViewMode === "list" ? "var(--as)" : "var(--bi)",
                border: "none",
                color: agViewMode === "list" ? "var(--ac)" : "var(--tm)",
                cursor: "pointer",
              }}
              onClick={() => {
                setAgViewMode("list");
              }}
            >
              <List size={14} />
            </button>
            <button
              style={{
                padding: "3px 7px",
                background:
                  agViewMode === "calendar" ? "var(--as)" : "var(--bi)",
                border: "none",
                color: agViewMode === "calendar" ? "var(--ac)" : "var(--tm)",
                cursor: "pointer",
              }}
              onClick={() => {
                setAgViewMode("calendar");
                requestAnimationFrame(() => {
                  const el = document.getElementById("ag-cal-current");
                  if (el)
                    el.scrollIntoView({ block: "start", behavior: "auto" });
                });
              }}
            >
              <Grid3X3 size={14} />
            </button>
          </div>
          {(_perms.viewer?.isAdmin ||
            _perms.can("caseView.agenda.edit") ||
            _perms.can("lateral.agenda")) && (
            <>
              <button
                className="btn btn-ghost"
                style={{
                  marginLeft: "auto",
                  padding: "3px 9px",
                  fontSize: FS.s11,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 5,
                }}
                title={t("agenda.btn.import_outlook_tip")}
                onClick={() => setOutlookModalOpen(true)}
              >
                <CalendarPlus size={12} />
                {t("agenda.btn.import_outlook")}
              </button>
              <AddButton
                label={t("common.add_button.add", { fallback: "Add" })}
                onClick={() => om("ag")}
                style={{ padding: "3px 9px" }}
              />
            </>
          )}
        </div>
        <EventTypeChips
          evtTypes={evtTypes}
          events={ag}
          value={agTypeFilt}
          onChange={setAgTypeFilt}
          multi={agMulti}
          onMulti={() => {
            setAgMulti((v: any) => !v);
            setAgTypeFilt(null);
          }}
          t={t}
        />
        <div
          style={{
            display: "flex",
            gap: 3,
            marginBottom: 8,
            alignItems: "center",
          }}
        >
          {[
            ["all", t("agenda.range.all")],
            ["week", t("agenda.range.this_week")],
            ["15d", t("agenda.range.next_15")],
            ["month", t("agenda.range.this_month")],
            ["30d", t("agenda.range.next_30")],
          ].map(([k, l]) => (
            <button
              key={k}
              style={{
                fontSize: FS.s9_5,
                padding: "2px 8px",
                borderRadius: RAD.r10,
                border: "none",
                background: agDateRange === k ? "var(--as)" : "var(--bi)",
                color: agDateRange === k ? "var(--ac)" : "var(--ts)",
                cursor: "pointer",
                fontFamily: "inherit",
              }}
              onClick={() => setDateRange(k)}
            >
              {l}
            </button>
          ))}
          {/* 110 §4b — the counter reads beside the range it counts (right
              of "Next 30 Days"), not across the screen from it; the auto
              margin that pushed it to the far edge moves to the lock, which
              is the one control that stays there. Same muted style. */}
          <span style={{ fontSize: FS.s9_5, color: "var(--tm)", marginLeft: 6 }}>
            {t("agenda.showing_counts", {
              ev: sorted.length,
              tk: sortedTkCount,
            })}
          </span>
          <LockButton
            {...({} as any)}
            locked={agLocked}
            onToggle={() => setAgLocked((v: any) => !v)}
            style={{ marginLeft: "auto" }}
          />
        </div>
      </div>
      {agViewMode === "list" && (
        <>
          {!showEarlier && hiddenAgCount > 0 && (
            <div style={{ textAlign: "center", padding: "5px 0 6px" }}>
              <span
                className="link"
                style={{ fontSize: FS.s10_5, cursor: "pointer" }}
                onClick={() => sShowEarlier(true)}
              >
                {t("agenda.show_earlier", { count: hiddenAgCount })}
              </span>
            </div>
          )}
          {showEarlier && (
            <div style={{ textAlign: "center", padding: "5px 0 6px" }}>
              <span
                className="link"
                style={{ fontSize: FS.s10_5, cursor: "pointer" }}
                onClick={() => sShowEarlier(false)}
              >
                {t("agenda.hide_earlier")}
              </span>
            </div>
          )}
          {/* BRIEF 105 §4a — THE COLUMN TITLES ARE GONE. "EVENTS" and "TASKS
              DUE" over a `1fr 1fr` grid of event cards and task rows: the
              columns are self-evident and the strip cost a line plus two rules
              at the top of every list. `.agColHeaders` is gone from App.css
              with them — its only rule was the narrow-viewport `display: none`
              that hid this block. */}
          {/* 216 addendum 7 §11 (1) [RULED 84] — the page's list is ONE grid (App.css `.agPage`; its two track
              lists and its gap ride as custom properties): every day, list and line below is a subgrid of it */}
          <div className="agPage" data-ag-page="" style={AG_PAGE_VARS}>
          {(() => {
            let lastMonth = "";
            /* 132 §7 — `_dayIdx` NO LONGER DECIDES ANYTHING BY DEFAULT, and
               102 §5a's care about it is withdrawn here rather than left to
               argue with the code. That comment explained why the band
               alternated over CONSECUTIVE DAY GROUPS rather than by calendar
               parity — a real problem for a stripe, and no problem at all for
               what replaced it: the field is a property of the DAY (weekend or
               observed holiday), so a filtered `dateList` cannot break it.

               133 §4 — AND THAT IS WHY THE INDEX IS STILL HERE. The band is a
               firm setting now, OFF by default ("it stays gone"), and a firm
               that switches it on gets 102 §5a's own mechanic back —
               consecutive day GROUPS, not calendar parity, which is the half of
               that comment that was always right. So `_dayIdx` decides
               something again, for the firms that ask for it. */
            return dateList.map((dateStr, _dayIdx) => {
              const d = new Date(dateStr);
              const mk = _monthName(d.getMonth()) + " " + d.getFullYear();
              const showHeader = mk !== lastMonth;
              lastMonth = mk;
              const isPastDate = dateStr < todayStrAg;
              const isToday = dateStr === todayStrAg;
              /* 132 §7 — the day's own answer, from the two things App already
                 resolved. `holidays.on` returns [] whenever the year is not
                 covered, so an uncovered year draws weekends and no holidays
                 rather than pretending there are none. */
              const _agHol = holidays ? holidays.on(dateStr) : [];
              const isNonWorking =
                ((schedAxis ? isNonWorkingDow(schedAxis, d.getDay()) : false) ||
                  _agHol.length > 0) &&
                calDisp?.agendaNonWorking !== false;
              const _agHolName = _agHol.map((h: { name: string }) => h.name).join(" · ");
              /* 133 §4 — the band, back within a firm's reach and OFF by
                 default. It is the SECOND background on the same div, so the
                 two switches cannot both paint: a non-working day keeps its
                 field, and the band falls to the days that are not one. A firm
                 that turns the band on and the shading off gets exactly 102
                 §5a's screen back. */
              const isBanded =
                !!calDisp?.agendaAltBand && !isNonWorking && _dayIdx % 2 === 1;
              const dayEvs = evByDate[dateStr] || [];
              const dayTks = tkByDate[dateStr] || [];
              return (
                /* 216 addendum 7 §11 (1) [RULED 84] — the day's wrapper contributes no box of its own
                   (`display: contents`): the month header and the band are items of the page's grid */
                <div key={dateStr} className="agDay" style={{ display: "contents" }}>
                  {/* ── BRIEF 105 §4b + §4c — THE MONTH NAME, WITH THE RULE
                      THROUGH IT AND CENTRED ────────────────────────────────
                      Was: the name at the left, a `border-bottom` UNDER the
                      whole row. Now: one rule running along the row on both
                      sides of the name, with 16px of air either side of it.
                      §4c centres it between the two columns, which is what
                      `flex: 1` on each rule does by construction — the two
                      halves are equal whatever the name's length.
                      §4c REVERSES 102 §5a, which argued a centred marker and
                      the day bands were not complementary. Laurent has used it
                      and disagrees: "in practice it is cumbersome to have to
                      look far left to the date when one is busy inspecting the
                      tasks on the right." Use beats the argument. The bands
                      stay; both markers move to the centre. Ruled 2026-08-19,
                      see RULINGS.md. */}
                  {showHeader && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 16,
                        padding: "10px 0 4px",
                        marginTop: 10,
                        marginBottom: 6,
                        gridColumn: "1 / -1", // 216 addendum 7 §11 (1) — across every column of the page's grid
                      }}
                    >
                      <div style={{ flex: 1, borderBottom: "1px solid var(--br)" }} />
                      <span
                        style={{
                          fontSize: FS.s14,
                          fontWeight: 700,
                          color: "var(--tp)",
                          flexShrink: 0,
                        }}
                      >
                        {mk}
                      </span>
                      <div style={{ flex: 1, borderBottom: "1px solid var(--br)" }} />
                    </div>
                  )}
                  {/* THE FIELD IS THIS DIV — 102 §5a's one durable finding,
                      kept because it is the reason 132 §7 costs no layout
                      change either. It already wraps the day header, the wide
                      `1fr 1fr` grid AND the narrow fallback, so painting it
                      puts a day's events and its tasks on one field by
                      construction — the grid is INSIDE it, not beside it. The
                      8px of horizontal padding is cancelled by an equal
                      negative margin, so it bleeds past the content and NOTHING
                      MOVES: the rows sit exactly where they sat.
                      What changed is the CONDITION: `_dayIdx % 2` became "is
                      this a non-working day", and the holiday's name rides the
                      same div as a `title`. */}
                  <div
                    className={
                      isNonWorking ? "agNonWorking" : isBanded ? "agDayBand" : undefined
                    }
                    title={_agHolName || undefined}
                    style={{
                      /* 216 addendum 7 §11 (1) [RULED 84] — the day is a subgrid of the page spanning every
                         column; its padding and the negative margin below cancel at the edge tracks (measured:
                         the lines' columns at the same x on a banded day and a plain one) */
                      ...AG_DAY,
                      opacity: isPastDate && !isToday ? 0.5 : 1,
                      marginBottom: 4,
                      /* §5a — the 8px of band padding is cancelled by an equal
                         negative margin, so the band bleeds past the content and
                         NOTHING MOVES: the rows sit exactly where they sat.
                         BRIEF 104 §A3c — that arrangement is untouched by the
                         squaring below; only `borderRadius` was removed. */
                      padding: "4px 8px",
                      marginLeft: -8,
                      marginRight: -8,
                      /* BRIEF 104 §A3a — SQUARE CORNERS, and the reason is what
                         a band IS. Rounded corners say "this is one object you
                         can act on"; a day band groups things rather than
                         presenting one, and nothing here is clickable as a
                         whole. A field, not a card.
                         §A3b — the radius sat on EVERY day group, banded or not,
                         because only the background is conditional. So on an
                         unbanded group it was drawing rounded corners around a
                         transparent box: invisible then, invisible now. Checked
                         for anything else depending on it — nothing on this
                         wrapper clips, no child is positioned against it, and
                         `overflow` is not set here, so there is no corner for a
                         radius to have been masking. */
                    }}
                  >
                    {/* ── BRIEF 105 §4c — THE DAY MARKER CENTRES TOO ───────
                        It was `[28] [AUG] [TODAY] ————` — the date pinned to
                        the far left with one rule trailing off to the right.
                        Now it is `———— 28 AUG TODAY ————`, the same shape the
                        month name above it takes, and for the same reason: a
                        reader working in the right-hand TASKS column should not
                        have to travel to the left edge to learn which day he is
                        looking at. Two `flex: 1` rules make the halves equal
                        whatever the marker's width, so it sits on the grid's
                        centre line — which is the gap between the two columns.
                        `minWidth: 22` LEAVES with the left alignment it
                        existed for: it kept single- and double-digit dates
                        starting at one x, and under a centred marker it would
                        do the opposite — pad the group asymmetrically and push
                        a 1-digit day off centre by half of it. */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        marginBottom: 3,
                        gridColumn: "1 / -1", // 216 addendum 7 §11 (1) — across the day's every column
                      }}
                    >
                      <div style={{ flex: 1, borderBottom: AG_HAIRLINE }} />
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          flexShrink: 0,
                        }}
                      >
                        <div
                          style={{
                            fontSize: FS.s15,
                            fontWeight: 700,
                            color: isToday ? "var(--ac)" : "var(--tp)",
                            lineHeight: 1,
                          }}
                        >
                          {d.getDate()}
                        </div>
                        <div
                          style={{
                            fontSize: FS.s10,
                            fontWeight: 600,
                            color: isToday ? "var(--ac)" : "var(--ts)",
                            textTransform: "uppercase",
                          }}
                        >
                          {pm(dateStr)}
                        </div>
                        {isToday && (
                          <span
                            style={{
                              fontSize: FS.s8,
                              padding: "1px 6px",
                              borderRadius: RAD.r8,
                              background: "var(--as)",
                              color: "var(--ac)",
                              fontWeight: 700,
                            }}
                          >
                            {t("agenda.today")}
                          </span>
                        )}
                      </div>
                      <div style={{ flex: 1, borderBottom: AG_HAIRLINE }} />
                    </div>
                    {/* 216 addendum 7 §11 (1) [RULED 84] — THE DAY'S TWO LISTS, subgrids of the day on the
                        page's columns: side by side wide (the events on the first copy of the track list, the
                        tasks on the second), stacked narrow (App.css's breakpoint moves both onto the one copy).
                        ONE DOM at every width — the two stacks addendum 4 drew (`.agWideOnly` the grid,
                        `.agNarrowOnly` the column, one of them hidden) are gone with the per-day grids.
                        132 §7 — the field (the band above) still reaches both widths for free: it is painted on
                        the day wrapper, which holds the one layout. */}
                    <div data-ag-list="events" style={agList("events")}>
                      {dayEvs.map((ev: any) => renderEvRow(ev))}
                    </div>
                    <div data-ag-list="tasks" style={{ ...agList("tasks"), borderLeft: AG_HAIRLINE, paddingLeft: 12 }}>
                      {dayTks.map((l: TkLine) => renderTkRow(l))}
                    </div>
                  </div>
                </div>
              );
            });
          })()}
          </div>
        </>
      )}
      {agViewMode === "calendar" &&
        (() => {
          const now3 = new Date();
          const curY = now3.getFullYear();
          const curM = now3.getMonth();
          const monthOffsets = [-3, -2, -1, 0, 1, 2, 3];
          const allEvs = filtByDate;
          const renderMonth = (y: any, m: any, isCurrent: any) => {
            const firstDay = new Date(y, m, 1);
            const lastDay = new Date(y, m + 1, 0);
            const startDow = firstDay.getDay() || 7;
            const totalDays = lastDay.getDate();
            const cells = [];
            for (let i = 1; i < startDow; i++) cells.push(null);
            for (let d2 = 1; d2 <= totalDays; d2++) cells.push(d2);
            while (cells.length % 7) cells.push(null);
            return (
              <div
                key={y + "-" + m}
                id={isCurrent ? "ag-cal-current" : undefined}
                style={{ marginBottom: 16 }}
              >
                <div
                  style={{
                    fontSize: FS.s13,
                    fontWeight: 700,
                    color: "var(--tp)",
                    padding: "8px 0 6px",
                    borderBottom: "1px solid var(--br)",
                    marginBottom: 4,
                  }}
                >
                  {_monthName(m)} {y}
                </div>
                {/* ── BRIEF 175 §2 [RULED 2026-09-09, Laurent] — THE CELLS PLAN ──
                       Three rules, one on each axis and one across the month.

                       HORIZONTALLY, follow the window. `minmax(0,1fr)` and not
                       `1fr`: an `fr` column takes an automatic minimum of
                       min-content, so a cell whose contents refuse to shrink can
                       WIDEN its column and push the other six. The `0` floor is
                       what makes "never widen a column" hold by construction
                       rather than by the contents happening to be small. Titles
                       already truncate rather than wrap (`minWidth: 0` +
                       ellipsis + nowrap on the span, `overflow: hidden` on the
                       row), and the full text stays in the `title` tooltip and
                       in search — the cut is CSS, never a slice.

                       VERTICALLY, follow the events, and EVERY EVENT IS SHOWN.
                       The `.slice(0, 4)` and the `+N` line below it are gone:
                       "no +2 more, no hidden rows".

                       ONE HEIGHT PER MONTH, SET BY THAT MONTH'S BUSIEST DAY.
                       `gridAutoRows: "1fr"` is what does it, and it is chosen
                       over arithmetic on the maximum event count deliberately: an
                       `fr` row track in a container of indefinite height resolves
                       to the LARGEST row's base size and every other row is made
                       equal to it, so the height is a property of the month and
                       no cell sizes itself. Arithmetic would need a per-event
                       pixel constant, which is a number that can be wrong in
                       every month at once and that only an eye would catch.
                       Each month renders its OWN grid, so the harmony is within
                       a month and the variation is between months, exactly as
                       ruled: a month with one crowded day is uniformly tall, an
                       empty one stays at the 56px floor.
                       ── This is judgment, not arithmetic: LAURENT'S EYES. ── */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(7,minmax(0,1fr))",
                    gridAutoRows: "1fr",
                    gap: 2,
                  }}
                >
                  {cells.map((day, i) => {
                    if (!day)
                      return (
                        <div
                          key={`e${y}${m}${i}`}
                          style={{
                            background: "rgba(255,255,255,.01)",
                            borderRadius: RAD.r6,
                            minHeight: 56,
                          }}
                        />
                      );
                    const ds = `${y}-${String(m + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
                    const isToday3 = ds === todayStrAg;
                    const isPast3 = ds < todayStrAg && !isToday3;
                    /* 132 §7 — RULED "in BOTH the list and calendar views", and
                       this is the calendar's half. The month grid navigates to
                       any year, which is why the lookup answers per date rather
                       than over a fixed window. */
                    const _calHol = holidays ? holidays.on(ds) : [];
                    /* 133 §4 — the same switch as the list half, read from the
                       same resolved object: "in BOTH the list and calendar
                       views" was one ruling and it stays one setting. The
                       alternating band has no calendar half — it never had one
                       — so `agendaAltBand` is not consulted here. */
                    const _calNonWorking =
                      ((schedAxis ? isNonWorkingDow(schedAxis, new Date(y, m, day).getDay()) : false) ||
                        _calHol.length > 0) &&
                      calDisp?.agendaNonWorking !== false;
                    const _calHolName = _calHol.map((h: { name: string }) => h.name).join(" · ");
                    const dayEvs2 = allEvs
                      .filter((a: any) => a.date && a.date.slice(0, 10) === ds)
                      .sort((a: any, b: any) =>
                        (a.time || "").localeCompare(b.time || ""),
                      );
                    return (
                      <div
                        key={ds}
                        /* 132 §7 — the class, not an inline colour: one
                           declaration serves both views and `--nwd` carries the
                           day/night direction. TODAY still outranks it — the
                           accent wash says "you are here", which is a stronger
                           claim than "the firm is closed". */
                        className={!isToday3 && _calNonWorking ? "agNonWorking" : undefined}
                        title={_calHolName || undefined}
                        style={{
                          background: isToday3
                            ? "var(--as)"
                            : _calNonWorking
                              ? undefined
                              : isPast3
                                ? "rgba(255,255,255,.005)"
                                : "rgba(255,255,255,.02)",
                          /* 148 §3.1 — the THIRD site carrying this exact
                             today-border, and the one the brief's coordinate
                             list missed. Not measured here (§3 named the two
                             14-day strips); recorded so the trio cannot drift.
                             The measurement lives at `matterListCfg.tsx`. */
                          border: `1px solid ${isToday3 ? "color-mix(in srgb, var(--ac) 50%, transparent)" : isPast3 ? "rgba(255,255,255,.04)" : "var(--br)"}`,
                          borderRadius: RAD.r6,
                          padding: "3px 4px",
                          minHeight: 56,
                          cursor: "pointer",
                        }}
                        onClick={() => {
                          setAgViewMode("list");
                        }}
                      >
                        <div
                          style={{
                            fontSize: FS.s10,
                            fontWeight: isToday3 ? 700 : 500,
                            color: isToday3 ? "var(--ac)" : "var(--ts)",
                            marginBottom: 2,
                            opacity: isPast3 ? 0.4 : 1,
                          }}
                        >
                          {day}
                        </div>
                        {/* 175 §2 — every event, always. Was `.slice(0, 4)`. */}
                        {dayEvs2.map((ev) => {
                          const cs5 = ev.caId
                            ? ca.find((c: any) => c.id === ev.caId)
                            : null;
                          const cc5 = ev.ccId
                            ? (cc || []).find((c: any) => c.id === ev.ccId)
                            : null;
                          const linkedTitle5 = cs5
                            ? cs5.title || ""
                            : cc5
                              ? cc5.title || ""
                              : "";
                          const _displayTitle5 = linkedTitle5 || ev.title || "";
                          /* BRIEF 102 §1a — the cut goes; the span that renders
                             it (:1755) already ellipsises. §1c's second step
                             DID bite here: it is a flex item at the default
                             `min-width: auto`, so it could not shrink and the
                             ellipsis had never once fired. `minWidth: 0` is
                             added there. */
                          const _tipParts5 = [
                            ev.title,
                            linkedTitle5,
                            cs5?.ref || cc5?.ref,
                            ev.notes,
                          ].filter(Boolean);
                          return (
                            <div
                              key={ev.id}
                              style={{
                                cursor: "pointer",
                                marginBottom: 1,
                                display: "flex",
                                alignItems: "center",
                                gap: 3,
                                overflow: "hidden",
                                opacity: isPast3 ? 0.5 : 1,
                              }}
                              title={_tipParts5.join(" — ")}
                              onClick={(e5) => {
                                e5.stopPropagation();
                                const _cs5 = ev.caId
                                  ? ca.find((c: any) => c.id === ev.caId)
                                  : null;
                                const _cc5 = ev.ccId
                                  ? (cc || []).find((c: any) => c.id === ev.ccId)
                                  : null;
                                if (_cs5 || _cc5) {
                                  if (_canNavCase(_cs5, _cc5)) {
                                    queueScrollToEv(ev.id);
                                    if (_cc5) nav("cc", _cc5.id);
                                    else nav("ca", _cs5.id);
                                  } else _navRefused(); // C-1
                                } else if (_canEditEv(ev)) om("ag", ev);
                                // C-1 — linked, not loaded, not editable:
                                // toast instead of the old silent nothing.
                                else if (ev.caId != null || ev.ccId != null)
                                  _navRefused();
                              }}
                            >
                              {etTag(ev.type)}
                              {ev.time && (
                                <span
                                  style={{
                                    fontSize: FS.s8,
                                    color: "var(--tm)",
                                    fontFamily: "'JetBrains Mono',monospace",
                                    flexShrink: 0,
                                  }}
                                >
                                  {ev.time}
                                </span>
                              )}
                              {_displayTitle5 && (
                                <span
                                  style={{
                                    fontSize: FS.s8_5,
                                    color: "var(--ts)",
                                    minWidth: 0,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {_displayTitle5}
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          };
          return (
            /* Phase 29 (item 6b) — lateral-scroll wrapper: at narrow
               effective widths (auto-zoom phones) the 7-col month grid
               scrolls sideways instead of being clipped by .C's
               overflow-x:hidden. List mode is untouched. */
            <div style={{ overflowX: "auto" }}>
              <div style={{ minWidth: 420 }}>
              <div
                style={{
                  // 175 §2 — the same column model as the month grid below, or
                  // the header stops lining up with the cells it names.
                  display: "grid",
                  gridTemplateColumns: "repeat(7,minmax(0,1fr))",
                  gap: 2,
                  marginBottom: 2,
                }}
              >
                {[1, 2, 3, 4, 5, 6, 7].map((d2) => (
                  <div
                    key={d2}
                    style={{
                      textAlign: "center",
                      fontSize: FS.s9,
                      color: "var(--tm)",
                      fontWeight: 600,
                      padding: "4px 0",
                    }}
                  >
                    {_shortDayName(d2)}
                  </div>
                ))}
              </div>
              {monthOffsets.map((off) => {
                let my = curM + off;
                let yy = curY;
                while (my < 0) {
                  my += 12;
                  yy--;
                }
                while (my > 11) {
                  my -= 12;
                  yy++;
                }
                return renderMonth(yy, my, off === 0);
              })}
              </div>
            </div>
          );
        })()}
      {outlookModalOpen && (
        <OutlookImportModal
          onClose={() => setOutlookModalOpen(false)}
          ca={ca}
          // 164 §B.2 — the collective cases join the picker.
          cc={cc as { id: number }[]}
          toast={toast}
          nav={nav}
          onImported={refreshAgenda}
        />
      )}
    </>
  );
};

export default AgView;
